import { useEffect, useMemo, useState } from "react";
import api, { fmtDate } from "@/lib/api";
import { LeaveCalendar, countWorkingDays } from "@/components/LeaveCalendar";
import { useLeaveVisible } from "@/hooks/useLeaveVisible";

const TYPES = ["Annual leave", "Sick leave", "Bereavement leave", "Public holiday"];
const countPH = (from, to, hols) => Object.keys(hols).filter(d => d >= from && d <= to && ![0, 6].includes(new Date(d + "T00:00:00").getDay())).length;
const errText = (e, f) => (typeof e?.response?.data?.detail === "string" ? e.response.data.detail : f);
const STATUS_CLS = { Pending: "text-amber-300", Approved: "text-green-400", Declined: "text-red-400", Cancelled: "opacity-50" };

export default function WorkerLeave() {
  const on = useLeaveVisible();
  if (on === null) return null;
  if (!on) return (
    <div className="card mt-2" data-testid="leave-under-construction">
      <h1 className="text-2xl mb-2">Leave</h1>
      <p>Under construction. Leave balances and requests are coming soon. Keep using PaySauce for leave until Luke tells you otherwise.</p>
    </div>
  );
  return <LeaveFlow />;
}

function LeaveFlow() {
  const [hols, setHols] = useState({});
  const [info, setInfo] = useState(null);
  const [mine, setMine] = useState([]);
  const [form, setForm] = useState({ type: TYPES[0], from: "", to: "", part: "", note: "" });
  const [msg, setMsg] = useState(""); const [err, setErr] = useState(""); const [busy, setBusy] = useState(false);

  const loadMine = () => api.get("/leave/mine").then(r => setMine(r.data));
  const [bal, setBal] = useState(null);
  useEffect(() => {
    api.get("/leave/my-balances").then(r => setBal(r.data)).catch(() => {});
  }, []);
  useEffect(() => {
    api.get("/public-holidays").then(r => setHols(Object.fromEntries(r.data.map(h => [h.date, h.name]))));
    api.get("/leave/my-info").then(r => setInfo(r.data));
    loadMine();
  }, []);

  const to = form.to || form.from;
  const single = form.from && to === form.from;
  const isPH = form.type === "Public holiday";
  const days = useMemo(() => (isPH ? countPH(form.from, to, hols) : countWorkingDays(form.from, to, hols)), [form.from, to, hols, isPH]);
  const dayH = single && parseFloat(form.part) > 0 ? parseFloat(form.part) : info?.day_hours || 8;

  async function submit() {
    setErr(""); setMsg("");
    if (!form.from) { setErr("Tap a FROM date on the calendar"); return; }
    setBusy(true);
    try {
      await api.post("/leave", { type: form.type, from_date: form.from, to_date: to, part_day_hours: single && parseFloat(form.part) > 0 ? parseFloat(form.part) : null, note: form.note });
      setMsg("Leave request sent ✓ Luke has been notified."); setForm({ type: form.type, from: "", to: "", part: "", note: "" }); loadMine();
    } catch (e) { setErr(errText(e, "Couldn't send the request")); }
    setBusy(false);
  }

  async function cancel(r) {
    if (!window.confirm("Cancel this leave request?")) return;
    try { await api.post(`/leave/${r.id}/cancel`); loadMine(); } catch (e) { setErr(errText(e, "Couldn't cancel")); }
  }

  if (info && !info.active) return <div className="card" data-testid="leave-inactive">Only active staff can request leave.</div>;
  return (
    <div className="space-y-4 pb-24" data-testid="worker-leave">
      <h1 className="text-2xl">Leave</h1>
      {bal && (
        <div className="card" data-testid="my-balances">
          <h2 className="text-silver mb-2">My leave balances (hours)</h2>
          <div className="grid grid-cols-2 gap-2 text-sm">
            <div data-testid="bal-annual">Annual<div className="text-lg">{bal.holiday_pay_type === "payg8" ? "8% with pay" : bal.annual_total.toFixed(2)}</div>
              {bal.annual_accrued > 0 && bal.leave_anniversary && <div className="text-[11px] opacity-60">{bal.annual_accrued.toFixed(2)} accrued, available from {fmtDate(bal.leave_anniversary)}</div>}</div>
            <div data-testid="bal-sick">Sick<div className="text-lg">{Number(bal.sick || 0).toFixed(2)}</div></div>
          </div>
        </div>
      )}
      <h2 className="text-lg">Request leave</h2>
      <div className="card space-y-3">
        <label className="text-sm block">Leave type
          <select className="input w-full mt-1" value={form.type} onChange={e => setForm({ ...form, type: e.target.value })} data-testid="leave-type">
            {TYPES.map(t => <option key={t}>{t}</option>)}
          </select></label>
        <div className="text-sm">Tap the first day, then the last day.</div>
        <LeaveCalendar hols={hols} from={form.from} to={form.to} onChange={(f, t) => setForm({ ...form, from: f, to: t, part: f === t || !t ? form.part : "" })} />
        <div className="text-sm" data-testid="leave-range">
          {form.from ? <>From <b>{fmtDate(form.from)}</b> to <b>{fmtDate(to)}</b></> : "No dates picked yet"}
        </div>
        {single && (
          <label className="text-sm block">Part day? Hours (optional)
            <input type="number" step="0.25" min="0.25" max="12" className="input w-32 ml-2" value={form.part} onChange={e => setForm({ ...form, part: e.target.value })} data-testid="leave-part-hours" /></label>
        )}
        <label className="text-sm block">Note (optional)
          <textarea className="input w-full mt-1 h-20" value={form.note} onChange={e => setForm({ ...form, note: e.target.value })} data-testid="leave-note" /></label>
        {form.from && (
          <div className="text-silver" data-testid="leave-total">
            {days} working day{days === 1 ? "" : "s"} · {(days * dayH).toFixed(2)} h
            <span className="block text-[11px] opacity-60">{single && parseFloat(form.part) > 0 ? "Part-day hours" : `${dayH} h per day (${info?.source || "default 8 h day"})`}.{isPH ? " Public holiday: paid, nothing comes off your leave. Only the marked public holidays count." : " Weekends and public holidays aren't counted."}</span>
          </div>
        )}
        {err && <div className="text-sm text-red-400" role="alert" data-testid="leave-error">{err}</div>}
        {msg && <div className="text-sm text-green-400" data-testid="leave-success">{msg}</div>}
        <button className="btn-silver w-full py-3" disabled={busy || !form.from || days === 0} onClick={submit} data-testid="leave-submit">{busy ? "Sending…" : "Send leave request"}</button>
      </div>
      <div className="card">
        <h2 className="text-silver mb-2">My leave requests</h2>
        {mine.length === 0 && <div className="text-sm opacity-50">No leave requests yet.</div>}
        <div className="space-y-2">{mine.map(r => (
          <div key={r.id} className="border border-white/10 rounded p-3 flex items-start justify-between gap-2" data-testid={`my-leave-${r.id}`}>
            <div className="text-sm">
              <div className="font-medium">{r.type}</div>
              <div className="opacity-80">{fmtDate(r.from_date)}{r.to_date !== r.from_date && ` – ${fmtDate(r.to_date)}`} · {r.working_days} day{r.working_days === 1 ? "" : "s"} · {r.hours} h</div>
              {r.decline_reason && <div className="text-xs text-red-300">Reason: {r.decline_reason}</div>}
            </div>
            <div className="text-right space-y-1">
              <div className={`text-sm ${STATUS_CLS[r.status] || ""}`} data-testid={`my-leave-status-${r.id}`}>{r.status}</div>
              {r.status === "Pending" && <button className="btn-ghost text-xs px-3 py-2" onClick={() => cancel(r)} data-testid={`my-leave-cancel-${r.id}`}>Cancel</button>}
            </div>
          </div>
        ))}</div>
      </div>
    </div>
  );
}
